import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Alert from '../../components/ui/Alert.jsx';
import AiMark, { isAiReason } from '../../components/ui/AiMark.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import { EmptyState, Loading, PageHeader } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { AnswerCard, PLAIN, currentAnswer, needsDecision, plainOf, toldText, whoOf } from './inboxShared.jsx';

// Messages: a list on the left, the chosen message on the right, in plain words, with
// the one question we need answered. Everything technical is on the full message page.

const VIEWS = [
  ['needs', 'Needs you', { decision: 'none' }, 'undecided'],
  ['scams', 'Scams', { verdict: 'scams' }, 'scams'],
  ['real', 'Really from you', { verdict: 'verified' }, 'genuine'],
  ['all', 'Everything', {}, 'total'],
];

const ANSWERS = [
  ['scam', 'It’s a scam', 'We warn anyone else who checks it, catch it at every event we protect, and tell the person who sent it to us.'],
  ['official', 'It’s really from us', 'We tell them it’s genuine, and learn it for next time.'],
  ['not_related', 'Nothing to do with us', 'We tell them it isn’t about your event, so they can deal with it themselves.'],
];

function MessagePanel({ organisationId, id, onDecided }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [answer, setAnswer] = useState(null);
  const base = `/portal/organisations/${organisationId}/inbox/${id}`;
  useEffect(() => {
    setData(null); setError('');
    api(base).then(setData).catch((err) => setError(err.message));
  }, [base]);

  if (error) return <section className="card msg-panel"><Alert tone="danger">{error}</Alert></section>;
  if (!data) return <section className="card msg-panel"><Loading /></section>;
  const m = { ...data.message, subject: data.original?.subject };
  const plain = plainOf(m);
  const reasons = (data.analysis?.reasons || []).filter((r) => r.text && ['scam', 'caution', 'good'].includes(r.severity)).slice(0, 5);
  const told = toldText(data.message.reply);
  const isCall = m.report_kind === 'approach';
  const decided = m.decision && m.decision !== 'unsure';
  const picked = ANSWERS.find(([k]) => k === answer);

  return (
    <section className="card msg-panel" aria-labelledby="msg-h">
      <span className={`msg-pill tone-${plain.tone}`}>{plain.label}</span>
      <h2 id="msg-h" className="msg-subject">{m.subject || (isCall ? 'A call or message someone reported' : '(no subject)')}</h2>
      <p className="muted">{whoOf(m)}{m.event ? ` · ${m.event.name}` : ''} · {fmtDateTime(m.received_at)}</p>

      <div className={`msg-found tone-${plain.tone}`}>
        <h3>{plain.headline}</h3>
        {reasons.length > 0 && <ul>{reasons.map((r, i) => <li key={`${r.code}-${i}`}>{isAiReason(r) && <AiMark />}{r.text}</li>)}</ul>}
        {!reasons.length && data.ai?.summary && <p><AiMark />{data.ai.summary}</p>}
        {(reasons.some(isAiReason) || (!reasons.length && data.ai?.summary)) && <p className="ai-note"><AiMark /> marks what AI worked out. Our own checks decide the verdict.</p>}
      </div>

      {told && (
        <div className="msg-told">
          <p className="msg-told-title">We’ve already told the person who asked</p>
          <p className="muted">{told}</p>
        </div>
      )}

      {data.can_decide && !decided && !isCall && (
        <>
          <h3 className="msg-question">{needsDecision(m) ? 'Is this email from you?' : 'Is that right?'}</h3>
          <div className={`msg-answers${currentAnswer(m) ? ' has-current' : ''}`}>
            {ANSWERS.map(([k, label, what]) => <AnswerCard key={k} k={k} title={label} what={what} current={currentAnswer(m)} onClick={() => setAnswer(k)} />)}
          </div>
          <p className="muted small">Your answer goes to the person who asked, and teaches us for next time. Not sure? <Link to={`/inbox/${id}`}>Look at it in full</Link>.</p>
        </>
      )}
      {data.can_decide && !decided && isCall && (
        <p><Link to={`/inbox/${id}`} className="btn btn-primary">Tell us about this call</Link></p>
      )}
      {decided && <p className="msg-decided">{PLAIN[m.decision]?.headline || 'You’ve answered this one.'} <Link to={`/inbox/${id}`}>Change your answer</Link></p>}
      {!data.can_decide && !decided && needsDecision(m) && <p className="muted">An owner or admin of your company needs to answer this one.</p>}

      <p className="msg-more"><Link to={`/inbox/${id}`}>See the email itself and the technical checks →</Link></p>

      {picked && (
        <ConfirmModal title={`${picked[1]}?`} confirmLabel={picked[1]} danger={picked[0] === 'scam'} onClose={() => setAnswer(null)}
          onConfirm={async () => {
            await api(`${base}/decision`, { method: 'POST', body: { decision: picked[0] } });
            setAnswer(null);
            onDecided();
          }}>
          <p>{picked[2]}</p>
        </ConfirmModal>
      )}
    </section>
  );
}

export default function Inbox() {
  const { organisation } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice || '');

  // Old links (?decision=none, ?verdict=…) still land on the right view
  const view = params.get('view') || (params.get('decision') === 'none' ? 'needs' : params.get('verdict') === 'verified' ? 'real' : params.get('verdict') ? 'scams' : 'all');
  const filter = VIEWS.find(([k]) => k === view)?.[2] || {};
  const eventId = params.get('event_id') || '';
  const page = Number(params.get('page')) || 1;
  const listQuery = new URLSearchParams({ ...filter, ...(eventId ? { event_id: eventId } : {}), ...(page > 1 ? { page: String(page) } : {}) }).toString();

  useEffect(() => {
    if (!organisation) return;
    api(`/portal/organisations/${organisation.id}/inbox${listQuery ? `?${listQuery}` : ''}`).then(setData).catch((err) => setError(err.message));
  }, [organisation, listQuery, reload]);

  const setParam = (changes) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    next.delete('decision'); next.delete('verdict');
    setParams(next, { replace: true });
  };
  const messages = data?.messages || [];
  const openId = Number(params.get('open')) || messages[0]?.id || null;

  if (error) return <div className="page"><PageHeader title="Messages" /><Alert tone="danger">{error}</Alert></div>;

  return (
    <div className="page">
      <PageHeader title="Messages"
        actions={<Link to="/inbox/add" className="btn btn-primary">Add a scam you were sent</Link>}
        subtitle="Emails, calls and questions your exhibitors and attendees asked us to check. We answer them straight away when we can tell. When we can’t, we ask you, and pass your answer on." />

      {notice && <Alert tone="success">{notice}</Alert>}

      <div className="msg-toolbar">
        <nav aria-label="Show" className="msg-views">
          {VIEWS.map(([k, label, , countKey]) => (
            <button key={k} type="button" className={`msg-view${view === k ? ' is-on' : ''}`} aria-pressed={view === k}
              onClick={() => setParam({ view: k === 'all' ? '' : k, open: '', page: '' })}>
              {label}{data ? ` (${data.counts[countKey] ?? 0})` : ''}
            </button>
          ))}
        </nav>
        {data?.events?.length > 1 && (
          <label className="filter"><span className="sr-only">Event</span>
            <select value={eventId} onChange={(e) => setParam({ event_id: e.target.value, open: '', page: '' })}>
              <option value="">All events</option>
              {data.events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </label>
        )}
      </div>

      {!data ? <Loading /> : !messages.length ? (
        <section className="card">
          <EmptyState title={view === 'needs' ? 'Nothing needs you' : 'Nothing here yet'}>
            {view === 'needs' ? 'When we can’t tell whether something is really from you, it appears here.' : 'When someone sends us an email or a call to check for one of your events, it appears here with what we found.'}
          </EmptyState>
        </section>
      ) : (
        <div className="msg-layout">
          <section className="card msg-list" aria-label="Messages">
            {messages.map((m) => {
              const plain = plainOf(m);
              return (
                <button key={m.id} type="button" className={`msg-row${m.id === openId ? ' is-open' : ''}`} aria-current={m.id === openId ? 'true' : undefined}
                  onClick={() => setParam({ open: String(m.id) })}>
                  <span className="msg-row-top">
                    <span className={`msg-pill tone-${plain.tone}`}>{needsDecision(m) ? `${plain.label} · needs you` : plain.label}</span>
                    <span className="muted small">{fmtDateTime(m.received_at)}</span>
                  </span>
                  <span className="msg-row-subject">{m.report_kind === 'approach' ? 'A call or message someone reported' : m.subject || '(no subject)'}</span>
                  <span className="muted msg-row-who">{whoOf(m)}{m.times_reported > 1 ? ` · reported ${m.times_reported} times` : ''}</span>
                </button>
              );
            })}
            {(page > 1 || data.has_more) && (
              <div className="pager">
                {page > 1 && <button type="button" className="btn btn-secondary btn-small" onClick={() => setParam({ page: String(page - 1), open: '' })}>Previous</button>}
                <span className="muted small">Page {page}</span>
                {data.has_more && <button type="button" className="btn btn-secondary btn-small" onClick={() => setParam({ page: String(page + 1), open: '' })}>Next</button>}
              </div>
            )}
          </section>
          {openId && <MessagePanel key={`${openId}-${reload}`} organisationId={organisation.id} id={openId}
            onDecided={() => { setNotice('Saved. We’ve passed your answer on.'); setReload((n) => n + 1); }} />}
        </div>
      )}
    </div>
  );
}
